import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

/**
 * Per-tenant AI assistant usage and spend (#861).
 *
 * Reads GET /api/v1/admin/assistant/usage, which is platform_admin ONLY: it
 * reports across tenants, so a customer_admin has no business calling it. The
 * hook does not send the request at all unless the signed-in user is a platform
 * admin — the server would 403 anyway, and a 403 per page load is noise in the
 * security log, not a defence.
 */
export interface TenantDayUsage {
  day: string;
  messages: number;
  input_tokens: number;
  output_tokens: number;
  cache_write_tokens: number;
  cache_read_tokens: number;
  estimated_usd: number;
}

export interface TenantUsage {
  customer_id: string;
  name: string;
  assistant_enabled: boolean;
  ceiling_usd: number;
  ceiling_is_default: boolean;
  today_usd: number;
  total_usd: number;
  total_tokens: number;
  models: string[];
  priced_with_fallback: boolean;
  over_ceiling: boolean;
  days: TenantDayUsage[];
}

export interface AssistantUsageLedger {
  days: number;
  today: string;
  default_ceiling_usd: number;
  /** True when migration 087 is not applied here: the zeros mean "unread", not "unspent". */
  schema_pending: boolean;
  estimate_note: string;
  tenants: TenantUsage[];
}

export const ASSISTANT_USAGE_WINDOW_DAYS = 30;
/** Spend moves slowly and the sweep is hourly; a minute of staleness costs nothing. */
export const ASSISTANT_USAGE_STALE_MS = 60 * 1000;

export function useAssistantUsage(days: number = ASSISTANT_USAGE_WINDOW_DAYS) {
  const role = useAuthStore((s) => s.user?.role);
  const isPlatformAdmin = role === 'platform_admin';

  return useQuery({
    queryKey: ['assistant', 'usage', days] as const,
    queryFn: () =>
      api
        .get<AssistantUsageLedger>(`/v1/admin/assistant/usage?days=${days}`)
        .then((r) => r.data),
    enabled: isPlatformAdmin,
    staleTime: ASSISTANT_USAGE_STALE_MS,
    retry: false,
  });
}
